import { type FinplanRow } from '@fin/shared';
import { scaleLinear } from 'd3-scale';
import { line } from 'd3-shape';
import { useEffect, useRef, useState } from 'react';
import { cx } from '../../components/ui.tsx';


import { plural, rub } from '../../lib/format.ts';




export function fmtAxis(v: number, narrow: boolean): string {
  const n = (x: number) => x.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
  if (v >= 1e9) return `${n(v / 1e9)} млрд`;
  if (v >= 1e6) return `${n(v / 1e6)}${narrow ? 'м' : ' млн'}`;
  if (v >= 1e3) return `${Math.round(v / 1e3)}${narrow ? 'к' : ' тыс'}`;
  return String(v);
}

export function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

export function CapitalChart({ rows, year, onYear }: { rows: FinplanRow[]; year: number | null; onYear: (y: number) => void }) {
  const [box, width] = useWidth<HTMLDivElement>();
  const narrow = width > 0 && width < 520;
  const W = Math.max(280, width || 760), H = narrow ? 220 : 260;
  const [hover, setHover] = useState<FinplanRow | null>(null);
  const maxY = Math.max(1, ...rows.map((r) => r.totalCapital));
  const yTicks = scaleLinear().domain([0, maxY]).nice().ticks(narrow ? 4 : 5);
  // the left margin fits the longest axis label (12px mono is about 7.3px a character)
  const m = { t: 12, r: 12, b: 26, l: Math.ceil(Math.max(...yTicks.map((t) => fmtAxis(t, narrow).length)) * 7.3) + 12 };
  const x = scaleLinear().domain([rows[0]?.age ?? 0, rows[rows.length - 1]?.age ?? 1]).range([m.l, W - m.r]);
  const y = scaleLinear().domain([0, maxY]).nice().range([H - m.b, m.t]);
  const total = line<FinplanRow>().x((r) => x(r.age)).y((r) => y(Math.max(0, r.totalCapital)))(rows) ?? '';
  const real = line<FinplanRow>().x((r) => x(r.age)).y((r) => y(Math.max(0, r.realCapital)))(rows) ?? '';
  const fmt = (v: number) => fmtAxis(v, narrow);
  const last = rows[rows.length - 1];
  const chosen = rows.find((r) => r.year === year) ?? last;
  const shown = hover ?? chosen;
  const nearest = (clientX: number, el: SVGSVGElement) => {
    const rect = el.getBoundingClientRect();
    const age = x.invert(((clientX - rect.left) / rect.width) * W);
    return rows.reduce((a, b) => (Math.abs(b.age - age) < Math.abs(a.age - age) ? b : a), rows[0]);
  };
  // a mouse previews, a press or a drag picks the year for the whole page
  const pick = (clientX: number, el: SVGSVGElement) => { const r = nearest(clientX, el); setHover(null); onYear(r.year); };
  // milestones every five years of age, and the last year: a phone reads these more easily than the curve
  const milestones = rows.filter((r, i) => r.age % 5 === 0 || i === rows.length - 1);

  return (
    <figure className="chart">
      <figcaption>
        <span>Итоговый капитал к {shown?.age} годам ({shown?.year})</span>
        <strong className="money">{rub(shown?.totalCapital)}</strong>
        <span className="muted small">в сегодняшних деньгах — <span className="money">{rub(shown?.realCapital)}</span>, пассивный доход <span className="money">{rub(shown?.passiveMonth)}</span> в месяц</span>
      </figcaption>
      <div ref={box} className="chart-box">
        {width ? (
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Итоговый капитал по возрасту"
            onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHover(null); }}
            onPointerDown={(e) => pick(e.clientX, e.currentTarget)}
            onPointerMove={(e) => { if (e.buttons) pick(e.clientX, e.currentTarget); else if (e.pointerType === 'mouse') setHover(nearest(e.clientX, e.currentTarget)); }}>
            {yTicks.map((t) => (
              <g key={t}>
                <line x1={m.l} x2={W - m.r} y1={y(t)} y2={y(t)} className="grid" />
                <text x={m.l - 6} y={y(t)} className="axis" textAnchor="end" dominantBaseline="middle">{fmt(t)}</text>
              </g>
            ))}
            {x.ticks(Math.min(narrow ? 5 : 10, rows.length)).map((t) => (
              <text key={t} x={x(t)} y={H - 6} className="axis" textAnchor="middle">{t}</text>
            ))}
            <path d={real} className="line-real" />
            <path d={total} className="line-total" />
            {shown ? <line x1={x(shown.age)} x2={x(shown.age)} y1={m.t} y2={H - m.b} className="chart-cursor" /> : null}
            {shown ? <circle cx={x(shown.age)} cy={y(Math.max(0, shown.totalCapital))} r={5} className="dot-total" /> : null}
          </svg>
        ) : <div style={{ height: H }} />}
      </div>
      <div className="chart-legend"><span><i className="lg-total" />Номинально</span><span><i className="lg-real" />С поправкой на инфляцию</span></div>
      {rows.length > 1 ? (
        <label className="year-slider">
          <span className="muted small">Год и возраст</span>
          <input type="range" min={rows[0].year} max={rows[rows.length - 1].year} step={1} value={chosen?.year ?? rows[0].year}
            onChange={(e) => onYear(Number(e.target.value))} aria-valuetext={chosen ? `${chosen.year}, ${chosen.age} ${plural(chosen.age, 'год', 'года', 'лет')}` : undefined} />
          <span className="year-slider-value"><strong>{chosen?.year}</strong> · {chosen ? `${chosen.age} ${plural(chosen.age, 'год', 'года', 'лет')}` : ''}</span>
        </label>
      ) : null}
      <div className="milestones chips" role="list" aria-label="Капитал по возрасту">
        {milestones.map((r) => (
          <button key={r.year} type="button" role="listitem" className={cx('milestone', chosen?.year === r.year && 'on')} onClick={() => onYear(r.year)} aria-pressed={chosen?.year === r.year}>
            <span className="milestone-age">{r.age} {plural(r.age, 'год', 'года', 'лет')}</span>
            <span className="money">{fmt(Math.round(r.totalCapital))}</span>
          </button>
        ))}
      </div>
    </figure>
  );
}
